import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useQueryClient } from '@tanstack/react-query';
import { getRouteApi, useLoaderData } from '@tanstack/react-router';

import { getMediaOptions, useProjectMedia } from '@/api/media';
import type { UploadMediaModalType } from '@/features/estate-media/components/upload-media-modal';
import {
  ACCEPTED_IMAGE_INPUT_TYPE,
  uploadImagesSchema,
} from '@/features/estate-media/schema/upload-media';
import { canWrite } from '@/features/permissions/resource-permissions';

import { useDeleteImage } from '../api/delete-image';
import { useUploadImage } from '../api/upload-image';
import { MediaData } from './media-data';
import { useModal } from '@/components/modals/new/use-modal';
import { ConfirmationModal } from '@/components/modals/confirmation-modal';
import { SelectThumbnailModal } from '@/features/estates-property/components/select-thumbnail-modal';
import { UploadMediaModal } from '../../estate-media/components/upload-media-modal';

const route = getRouteApi(
  '/_authenticated/_sidebar-layout/projects/$projectId/_dashboard-layout/media/images'
);

export const MediaImagesDashboard = () => {
  const { projectId } = route.useParams();

  const uploadModal = useModal();
  const thumbnailModal = useModal();
  const deleteModal = useModal();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [deleteCallback, setDeleteCallback] = useState<() => void>(() => {});
  const { t } = useTranslation();

  const queryClient = useQueryClient();
  const { data } = useProjectMedia({ id: projectId });
  const project = useLoaderData({
    from: '/_authenticated/_sidebar-layout/projects/$projectId/_dashboard-layout',
  });

  const canWriteMedia = canWrite(project.metadata?.permissions, 'media');
  const images = Object.values(data.images).flat();

  const { mutateAsync: deleteImage } = useDeleteImage({
    mutationConfig: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getMediaOptions(projectId).queryKey });
        toast.success(t('successfullyAddedImage'));
      },
    },
  });

  const { mutateAsync: uploadImage } = useUploadImage({
    mutationConfig: {
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: getMediaOptions(projectId).queryKey }),
    },
  });

  const handleDelete = (ids: string[], callback = () => {}) => {
    setSelectedIds(ids);
    setDeleteCallback(() => callback);
    deleteModal.open();
  };

  const submitHandler = async (data: UploadMediaModalType) => {
    const dataTransfer = new DataTransfer();
    data.files.forEach((file) => dataTransfer.items.add(file));
    await uploadImage({ images: dataTransfer.files, projectId });
    uploadModal.dismiss();
  };

  const handleOpenModal = () => {
    uploadModal.open();
  };

  return (
    <>
      <MediaData
        data={data.images}
        canWriteMedia={canWriteMedia}
        onUpload={handleOpenModal}
        handleDelete={handleDelete}
        onChooseThumbnail={thumbnailModal.open}
        isThumbnailDisabled={!images.length}
        page="images"
        type="project"
        projectId={projectId}
      />
      <SelectThumbnailModal modal={thumbnailModal} type="project" images={images} id={projectId} />
      <UploadMediaModal
        modal={uploadModal}
        submitHandler={submitHandler}
        acceptedType={ACCEPTED_IMAGE_INPUT_TYPE}
        acceptedMediaSchema={uploadImagesSchema()}
      />
      <ConfirmationModal
        modal={deleteModal}
        title={`${t('delete')} ${t('image')} `}
        description={t('confirmDeleteDocument')}
        confirmationText={t('delete')}
        destructive
        onSubmit={async () => {
          await deleteImage({ projectId, imageIds: selectedIds });
          deleteCallback();
        }}
      />
    </>
  );
};
